import React from 'react';
import {
  Icon,
  Pressable,
  ScreenContainer,
  SimpleStyleFlatList,
  VideoPlayer,
  withTheme,
} from '@draftbit/ui';
import { useIsFocused } from '@react-navigation/native';
import { BlurView } from 'expo-blur';
import { ActivityIndicator, Text, View } from 'react-native';
import { Fetch } from 'react-request';
import * as GlobalStyles from '../GlobalStyles.js';
import * as LeadFunlDefaultApi from '../apis/LeadFunlDefaultApi.js';
import palettes from '../themes/palettes';
import Breakpoints from '../utils/Breakpoints';
import * as StyleSheet from '../utils/StyleSheet';
import imageSource from '../utils/imageSource';
import useWindowDimensions from '../utils/useWindowDimensions';

const TutorialsScreen = props => {
  const { theme, navigation } = props;
  const dimensions = useWindowDimensions();
  const [isFocused, setIsFocused] = React.useState({});
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [playingVideoUrl, setPlayingVideoUrl] = React.useState('');

  return (
    <ScreenContainer hasSafeArea={false} scrollable={false}>
      {/* Header */}
      <View
        style={StyleSheet.applyWidth(
          {
            alignItems: 'center',
            borderBottomWidth: 1,
            borderColor: palettes.Brand.Divider,
            flexDirection: 'row',
            height: 48,
            justifyContent: 'space-between',
          },
          dimensions.width
        )}
      >
        {/* Back */}
        <Pressable
          onPress={() => {
            try {
              navigation.navigate('ProfileScreen');
            } catch (err) {
              console.error(err);
            }
          }}
          activeOpacity={0.3}
        >
          <View
            style={StyleSheet.applyWidth(
              {
                alignItems: 'center',
                height: 48,
                justifyContent: 'center',
                width: 48,
              },
              dimensions.width
            )}
          >
            <Icon
              size={24}
              color={theme.colors.branding.primary}
              name={'Ionicons/chevron-back'}
            />
          </View>
        </Pressable>

        <Text
          accessible={true}
          selectable={false}
          {...GlobalStyles.TextStyles(theme)['Text'].props}
          style={StyleSheet.applyWidth(
            StyleSheet.compose(GlobalStyles.TextStyles(theme)['Text'].style, {
              color: theme.colors.branding.primary,
              flex: 1,
              fontFamily: [
                { minWidth: Breakpoints.Mobile, value: 'Aclonica_400Regular' },
                { minWidth: Breakpoints.Tablet, value: 'Aclonica_400Regular' },
              ],
              fontSize: 21,
              marginRight: 48,
              textAlign: 'center',
            }),
            dimensions.width
          )}
        >
          {'Tutorials'}
        </Text>
      </View>
      {/* Container */}
      <View
        style={StyleSheet.applyWidth(
          { alignItems: 'center', flex: 1 },
          dimensions.width
        )}
      >
        <LeadFunlDefaultApi.FetchTutorialsGET>
          {({ loading, error, data, refetchTutorials }) => {
            const fetchData = data?.json;
            if (loading) {
              return <ActivityIndicator />;
            }

            if (error || data?.status < 200 || data?.status >= 300) {
              return <ActivityIndicator />;
            }

            return (
              <SimpleStyleFlatList
                data={(() => {
                  const e = fetchData;
                  console.log(e);
                  return e;
                })()}
                decelerationRate={'normal'}
                horizontal={false}
                inverted={false}
                keyExtractor={(listData, index) =>
                  listData?.id ??
                  listData?.uuid ??
                  index?.toString() ??
                  JSON.stringify(listData)
                }
                keyboardShouldPersistTaps={'never'}
                listKey={'Container->Fetch->List'}
                nestedScrollEnabled={false}
                onEndReachedThreshold={0.5}
                pagingEnabled={false}
                renderItem={({ item, index }) => {
                  const listData = item;
                  return (
                    <View
                      style={StyleSheet.applyWidth(
                        {
                          borderColor: {
                            minWidth: Breakpoints.Tablet,
                            value: palettes.Brand.Divider,
                          },
                          justifyContent: {
                            minWidth: Breakpoints.Tablet,
                            value: 'center',
                          },
                          marginBottom: 10,
                          marginTop: 10,
                          padding: 15,
                          paddingBottom: 10,
                          paddingTop: 10,
                        },
                        dimensions.width
                      )}
                    >
                      <Text
                        accessible={true}
                        selectable={false}
                        {...GlobalStyles.TextStyles(theme)['Text'].props}
                        style={StyleSheet.applyWidth(
                          StyleSheet.compose(
                            GlobalStyles.TextStyles(theme)['Text'].style,
                            {
                              fontFamily: 'Poppins_500Medium',
                              fontSize: 17,
                              marginBottom: 12,
                              textAlign: 'center',
                            }
                          ),
                          dimensions.width
                        )}
                      >
                        {listData?.title}
                      </Text>
                      {/* Video - Preview */}
                      <>
                        {listData?.video?.url === playingVideoUrl ? null : (
                          <VideoPlayer
                            isLooping={false}
                            isMuted={false}
                            playsInSilentModeIOS={false}
                            posterResizeMode={'cover'}
                            rate={1}
                            resizeMode={'cover'}
                            shouldPlay={false}
                            volume={0.5}
                            {...GlobalStyles.VideoPlayerStyles(theme)['Video']
                              .props}
                            posterSource={imageSource(
                              `${listData?.video?.url}`
                            )}
                            source={imageSource(`${listData?.video?.url}`)}
                            style={StyleSheet.applyWidth(
                              StyleSheet.compose(
                                GlobalStyles.VideoPlayerStyles(theme)['Video']
                                  .style,
                                { height: 250, width: 300 }
                              ),
                              dimensions.width
                            )}
                            useNativeControls={false}
                            usePoster={false}
                          />
                        )}
                      </>
                      {/* Video - Playing */}
                      <>
                        {!(listData?.video?.url === playingVideoUrl) ? null : (
                          <VideoPlayer
                            isLooping={false}
                            isMuted={false}
                            onPlaybackStatusUpdate={playbackStatus => {
                              try {
                                /* hidden 'Set Variable' action */
                                /* hidden 'Log to Console' action */
                              } catch (err) {
                                console.error(err);
                              }
                            }}
                            playsInSilentModeIOS={false}
                            posterResizeMode={'cover'}
                            rate={1}
                            useNativeControls={true}
                            usePoster={false}
                            volume={0.5}
                            {...GlobalStyles.VideoPlayerStyles(theme)['Video']
                              .props}
                            posterSource={imageSource(
                              `${listData?.video?.url}`
                            )}
                            resizeMode={'contain'}
                            shouldPlay={true}
                            source={imageSource(`${listData?.video?.url}`)}
                            style={StyleSheet.applyWidth(
                              StyleSheet.compose(
                                GlobalStyles.VideoPlayerStyles(theme)['Video']
                                  .style,
                                { height: 250, width: 300 }
                              ),
                              dimensions.width
                            )}
                          />
                        )}
                      </>
                      <>
                        {!(playingVideoUrl !== listData?.video?.url) ? null : (
                          <View
                            style={StyleSheet.applyWidth(
                              {
                                alignItems: 'stretch',
                                bottom: 0,
                                flexDirection: 'row',
                                justifyContent: 'center',
                                left: 0,
                                position: 'absolute',
                                right: 0,
                                top: 0,
                              },
                              dimensions.width
                            )}
                          >
                            <Pressable
                              onPress={() => {
                                try {
                                  /* hidden 'If/Else' action */
                                  setPlayingVideoUrl(listData?.video?.url);
                                } catch (err) {
                                  console.error(err);
                                }
                              }}
                              activeOpacity={0.3}
                              style={StyleSheet.applyWidth(
                                { height: '100%', width: '100%' },
                                dimensions.width
                              )}
                            >
                              <>
                                {!(
                                  listData !== isFocused && !isPlaying
                                ) ? null : (
                                  <BlurView
                                    experimentalBlurMethod={'none'}
                                    intensity={50}
                                    tint={'default'}
                                    {...GlobalStyles.BlurViewStyles(theme)[
                                      'Blur View'
                                    ].props}
                                    intensity={StyleSheet.getWidthValue(
                                      [
                                        {
                                          minWidth: Breakpoints.Tablet,
                                          value: 10,
                                        },
                                      ],
                                      dimensions.width
                                    )}
                                    style={StyleSheet.applyWidth(
                                      StyleSheet.compose(
                                        GlobalStyles.BlurViewStyles(theme)[
                                          'Blur View'
                                        ].style,
                                        {
                                          alignItems: 'center',
                                          flex: 1,
                                          justifyContent: 'center',
                                        }
                                      ),
                                      dimensions.width
                                    )}
                                  >
                                    <>
                                      {isPlaying ? null : (
                                        <Icon
                                          size={24}
                                          name={'Ionicons/play'}
                                          size={StyleSheet.getWidthValue(
                                            [
                                              {
                                                minWidth: Breakpoints.Tablet,
                                                value: 50,
                                              },
                                            ],
                                            dimensions.width
                                          )}
                                        />
                                      )}
                                    </>
                                  </BlurView>
                                )}
                              </>
                            </Pressable>
                          </View>
                        )}
                      </>
                    </View>
                  );
                }}
                showsHorizontalScrollIndicator={true}
                showsVerticalScrollIndicator={true}
                snapToAlignment={'start'}
                numColumns={dimensions.width >= Breakpoints.Tablet ? 2 : 1}
                style={StyleSheet.applyWidth(
                  {
                    alignItems: 'stretch',
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    justifyContent: [
                      { minWidth: Breakpoints.Mobile, value: 'center' },
                      { minWidth: Breakpoints.Tablet, value: 'center' },
                    ],
                  },
                  dimensions.width
                )}
              />
            );
          }}
        </LeadFunlDefaultApi.FetchTutorialsGET>
      </View>
    </ScreenContainer>
  );
};

export default withTheme(TutorialsScreen);
